/**
 * useChipListener — subscribes to chip-tap events from the Electron shell's
 * PC/SC bridge.
 *
 * History note: this hook previously had a second "HID keyboard" channel
 * that synthesised chip-tap events from rapid keystroke bursts. We dropped
 * it on 2026-05-15 — see commit history. Reasons:
 *   - HID readers can't be distinguished from real keyboards. No way to
 *     show "reader connected" status or fail gracefully if it's unplugged.
 *   - Focus rules forced odd behaviour (the listener had to ignore taps
 *     while inputs were focused, which broke admin enrolment flows).
 *   - Mixing two input paths doubled the surface area for bugs.
 *
 * The only supported reader is now PC/SC via nfc-pcsc, exposed to the
 * renderer through window.kiosk.onChipTap. Cheap HID readers can still be
 * used to TYPE a UID into an enrolment form (StaffChipManager) — they're
 * just keyboards into a text input, no special handling needed.
 *
 * Caller-controlled `enabled` flag lets dialogs toggle listening on/off
 * (e.g. the admin enrolment dialog only listens while it's open).
 */
import { useEffect, useRef, useState } from "react";

import type {
  ChipListenerStatus,
  ChipTapEvent,
  UseChipListenerOptions,
  UseChipListenerResult,
} from "../types/chipTypes";

const DEFAULT_MIN_LENGTH = 8;
const DEFAULT_MAX_LENGTH = 32;
const HEX_ONLY_RE = /^[0-9A-F]+$/;

function normaliseUid(raw: string): string {
  return raw.replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
}

export function useChipListener(
  options: UseChipListenerOptions = {},
): UseChipListenerResult {
  const {
    onTap,
    enabled = true,
    minLength = DEFAULT_MIN_LENGTH,
    maxLength = DEFAULT_MAX_LENGTH,
  } = options;

  const [status, setStatus] = useState<ChipListenerStatus>("idle");
  const [lastTap, setLastTap] = useState<ChipTapEvent | null>(null);
  const [readerName, setReaderName] = useState<string | null>(null);
  const [readerError, setReaderError] = useState<string | null>(null);

  // Hold the latest onTap in a ref so consumers don't have to memoise it.
  const onTapRef = useRef<typeof onTap>(onTap);
  useEffect(() => {
    onTapRef.current = onTap;
  }, [onTap]);

  useEffect(() => {
    if (!enabled) {
      setStatus("disabled");
      return;
    }
    const bridge = typeof window !== "undefined" ? window.kiosk : undefined;
    if (!bridge) {
      // We're outside the Electron shell (browser tab, dev server). No
      // PC/SC bridge to subscribe to.
      setStatus("no-bridge");
      return;
    }

    setStatus("electron-ready");

    bridge.onChipTap((payload) => {
      const normalised = normaliseUid(payload.uid);
      if (
        normalised.length < minLength ||
        normalised.length > maxLength ||
        !HEX_ONLY_RE.test(normalised)
      ) {
        return;
      }
      const event: ChipTapEvent = {
        uid: normalised,
        raw: payload.uid,
        source: "electron",
        ts: payload.ts,
      };
      setLastTap(event);
      onTapRef.current?.(event);
    });

    bridge.onReaderAttached((p) => {
      setReaderName(p.name);
      setReaderError(null);
    });
    bridge.onReaderDetached(() => setReaderName(null));
    bridge.onReaderError((p) => setReaderError(p.message));

    // Cold-start race recovery: nfc-pcsc fires 'reader' exactly once per
    // attach event. On a fresh launch the event lands ~2s after the
    // window opens — sometimes BEFORE this useEffect runs, so the
    // onReaderAttached subscription misses it and the kiosk shows
    // "Kein RFID-Leser" until the user unplugs+replugs the reader.
    // Ask main for its cached reader list and seed state if anything
    // is already attached.
    bridge.listAttachedReaders?.().then((names) => {
      if (Array.isArray(names) && names.length > 0) {
        setReaderName((current) => current ?? names[0]);
      }
    }).catch(() => { /* older shells without this IPC: just no-op */ });

    // Note: the preload's `on*` helpers don't expose unsubscribe today, so
    // we don't return a cleanup function. This is acceptable because the
    // kiosk app is single-page and these listeners live for the page lifetime.
    // If we move to multi-route navigation we'll need to refactor the bridge
    // to return disposers.
  }, [enabled, minLength, maxLength]);

  return { status, lastTap, readerName, readerError };
}
